import corporateConfig from '@/config/corporate';
import store from '@/store';
import { resolveTypeBusinessFromState } from './typeBusiness';

/**
 * Fonte única para saber se o ambiente corporativo está ativo.
 * Prioridade: company.typeBusiness (2=corp, 1=escola) → website.company.typeBusiness
 * → flags legadas → env.
 */
function toCorporateFlag(value) {
    if (value === undefined || value === null) return null;
    if (value === true || value === 1 || value === '1' || value === 'true') return true;
    if (value === false || value === 0 || value === '0' || value === 'false') return false;
    return null;
}

export function isCorporateEnvironment() {
    const typeBusiness = resolveTypeBusinessFromState(store.state);
    if (typeBusiness === 2) return true;
    if (typeBusiness === 1) return false;

    const settings = store.state?.corporateCompanySettings;
    const fromSettings = toCorporateFlag(settings?.isCorporate);
    if (fromSettings !== null) {
        return fromSettings;
    }
    const company = store.state?.company;
    if (company && typeof company === 'object' && !Array.isArray(company)) {
        const fromCompany = toCorporateFlag(company.isCorporate);
        if (fromCompany !== null) {
            return fromCompany;
        }
        if (company.platform_type === 'corporate') {
            return true;
        }
        if (company.platform_type === 'school') {
            return false;
        }
    }
    if (store.state?.corporateEnabled === true) {
        return true;
    }
    if (store.state?.corporateEnabled === false) {
        return false;
    }

    return Boolean(corporateConfig.enabled);
}

export function corporateFeatureEnabled(featureKey) {
    if (!isCorporateEnvironment()) {
        return false;
    }
    if (!featureKey) {
        return true;
    }
    const features = store.state?.corporateFeatures;
    if (features && Object.prototype.hasOwnProperty.call(features, featureKey)) {
        return Boolean(features[featureKey]);
    }
    return true;
}

const CORPORATE_MOCK_OVERRIDE_KEY = 'weeduc_corporate_mock_override';

if (typeof window !== 'undefined') {
    // Limpa override antigo do botão Mock/API assim que o módulo carrega.
    window.localStorage.removeItem(CORPORATE_MOCK_OVERRIDE_KEY);
}

/**
 * Mock corporativo desativado: estrutura, liberação e colaboradores
 * usam sempre os endpoints reais `/corporate/*`.
 */
export function isCorporateMockEnabled() {
    return false;
}

export function setCorporateMockEnabled() {
    // no-op: fluxo corporativo permanece em API real
    if (typeof window !== 'undefined') {
        window.localStorage.removeItem(CORPORATE_MOCK_OVERRIDE_KEY);
    }
}

export function clearCorporateMockOverride() {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(CORPORATE_MOCK_OVERRIDE_KEY);
}

export function hasCorporateMockOverride() {
    return false;
}

/** Responsável do setor via user.manager + idOrganisationalStructure (schema alinhado). */
export function supportsOrganizationalManagerFeatures() {
    return true;
}

/**
 * Site público habilitado.
 * Corporativo: `Website.status` (0 = inativo, 1 = ativo).
 */
export function isPublicSiteEnabled() {
    if (!isCorporateEnvironment()) {
        return true;
    }

    const website = store.state?.websites;
    if (website && typeof website === 'object' && !Array.isArray(website)
        && Object.prototype.hasOwnProperty.call(website, 'status')) {
        return toBoolFlag(website.status, false);
    }

    const settings = store.state?.corporateCompanySettings;
    if (settings && typeof settings.siteEnabled === 'boolean') {
        return settings.siteEnabled;
    }

    const company = store.state?.company;
    if (company && typeof company.siteEnabled === 'boolean') {
        return company.siteEnabled;
    }

    return false;
}

function toBoolFlag(value, fallback) {
    if (value === undefined || value === null) return fallback;
    return value === 1 || value === true || value === '1';
}

/**
 * Módulos do portal (Configurações corporativas) — controlam menu admin e portal.
 * Keys: 'site' | 'certificate' | 'inquiry' | 'forum'
 */
export function isCorporateModuleEnabled(moduleKey) {
    if (!isCorporateEnvironment()) {
        return true;
    }

    if (moduleKey === 'site') {
        return isPublicSiteEnabled();
    }

    const settings = store.state?.corporateCompanySettings;
    const portal = settings?.portal || {};
    const website = store.state?.websites;
    const websiteFlags = website && typeof website === 'object' && !Array.isArray(website)
        ? website
        : {};

    const defaults = {
        certificate: true,
        inquiry: false,
        forum: false,
    };

    if (moduleKey === 'certificate') {
        if (Object.prototype.hasOwnProperty.call(websiteFlags, 'showCertificate')) {
            return toBoolFlag(websiteFlags.showCertificate, defaults.certificate);
        }
        if (Object.prototype.hasOwnProperty.call(portal, 'showCertificate')) {
            return toBoolFlag(portal.showCertificate, defaults.certificate);
        }
        return defaults.certificate;
    }
    if (moduleKey === 'inquiry') {
        if (Object.prototype.hasOwnProperty.call(websiteFlags, 'showInquiry')) {
            return toBoolFlag(websiteFlags.showInquiry, defaults.inquiry);
        }
        if (Object.prototype.hasOwnProperty.call(portal, 'showInquiry')) {
            return toBoolFlag(portal.showInquiry, defaults.inquiry);
        }
        return defaults.inquiry;
    }
    if (moduleKey === 'forum') {
        if (Object.prototype.hasOwnProperty.call(websiteFlags, 'showForum')) {
            return toBoolFlag(websiteFlags.showForum, defaults.forum);
        }
        if (Object.prototype.hasOwnProperty.call(portal, 'showForum')) {
            return toBoolFlag(portal.showForum, defaults.forum);
        }
        return defaults.forum;
    }

    return true;
}

/**
 * Portal do aluno: typeBusiness 2 = layout corporativo, 1 = escolar.
 */
export function isPortalCorporateLayout() {
    return isCorporateEnvironment();
}

export default {
    isCorporateEnvironment,
    corporateFeatureEnabled,
    isCorporateMockEnabled,
    setCorporateMockEnabled,
    clearCorporateMockOverride,
    hasCorporateMockOverride,
    supportsOrganizationalManagerFeatures,
    isPublicSiteEnabled,
    isCorporateModuleEnabled,
    isPortalCorporateLayout,
};
